import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

export interface CourseChapter {
  id?: number;
  name: string;
  description?: string | null;
  thumbnailUrl?: string | null;
  sortOrder?: number;
}

export interface Course {
  id: number;
  name: string;
  level: string | null;
  chapters?: CourseChapter[];
  created_at?: string;
  updated_at?: string;
}

interface CoursesState {
  courses: Course[];
  currentCourse: Course | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  loading: boolean;
  error: string | null;
}

const initialState: CoursesState = {
  courses: [],
  currentCourse: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  loading: false,
  error: null,
};

const API_BASE_URL = import.meta.env.VITE_API_URL as string;
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchCoursesAsync = createAsyncThunk(
  'courses/fetchCourses',
  async ({ page = 1, limit = 10 }: { page?: number; limit?: number } = {}, { rejectWithValue, getState }) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      const response = await axios.get(`${API_BASE_URL}/courses?${params.toString()}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch courses'));
    }
  }
);

export const fetchCourseByIdAsync = createAsyncThunk(
  'courses/fetchCourseById',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/courses/${id}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.course as Course;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch course'));
    }
  }
);

export const createCourseAsync = createAsyncThunk(
  'courses/createCourse',
  async (payload: { name: string; level: string | null; chapters: CourseChapter[] }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/courses`, payload, {
        headers: getAuthHeaders(getState)
      });
      return response.data.course as Course;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create course'));
    }
  }
);

export const updateCourseAsync = createAsyncThunk(
  'courses/updateCourse',
  async ({ id, data }: { id: number; data: { name?: string; level?: string | null; chapters?: CourseChapter[] } }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/courses/${id}`, data, {
        headers: getAuthHeaders(getState)
      });
      return response.data.course as Course;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update course'));
    }
  }
);

export const createChapterAsync = createAsyncThunk(
  'courses/createChapter',
  async (
    { courseId, data }: { courseId: number; data: { name: string; description?: string | null; thumbnailUrl?: string | null; sortOrder?: number } },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/courses/${courseId}/chapters`, data, {
        headers: getAuthHeaders(getState)
      });
      return { courseId, chapter: response.data.chapter as CourseChapter };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create chapter'));
    }
  }
);

export const updateChapterAsync = createAsyncThunk(
  'courses/updateChapter',
  async (
    { courseId, chapterId, data }: { courseId: number; chapterId: number; data: { name?: string; description?: string | null; thumbnailUrl?: string | null; sortOrder?: number } },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/courses/${courseId}/chapters/${chapterId}`, data, {
        headers: getAuthHeaders(getState)
      });
      return { courseId, chapter: response.data.chapter as CourseChapter };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update chapter'));
    }
  }
);

export const deleteCourseAsync = createAsyncThunk(
  'courses/deleteCourse',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/courses/${id}`, {
        headers: getAuthHeaders(getState)
      });
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete course'));
    }
  }
);

const coursesSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCourse: (state) => {
      state.currentCourse = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoursesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoursesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.courses = action.payload.courses;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCoursesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCourseByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourseByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCourse = action.payload;
      })
      .addCase(fetchCourseByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createCourseAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCourseAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.courses.unshift(action.payload);
      })
      .addCase(createCourseAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateCourseAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCourseAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCourse = action.payload;
        const i = state.courses.findIndex(c => c.id === action.payload.id);
        if (i !== -1) state.courses[i] = action.payload;
      })
      .addCase(updateCourseAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createChapterAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createChapterAsync.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentCourse && state.currentCourse.id === action.payload.courseId) {
          const list = state.currentCourse.chapters || [];
          (state.currentCourse.chapters ||= []);
          list.push(action.payload.chapter);
          // ensure sort by sortOrder
          state.currentCourse.chapters = [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        }
      })
      .addCase(createChapterAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateChapterAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChapterAsync.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentCourse && state.currentCourse.id === action.payload.courseId) {
          const list = state.currentCourse.chapters || [];
          const idx = list.findIndex(c => c.id === action.payload.chapter.id);
          if (idx !== -1) {
            list[idx] = action.payload.chapter;
          } else {
            list.push(action.payload.chapter);
          }
          state.currentCourse.chapters = [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        }
      })
      .addCase(updateChapterAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteCourseAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCourseAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.courses = state.courses.filter(c => c.id !== action.payload);
        if (state.currentCourse?.id === action.payload) state.currentCourse = null;
      })
      .addCase(deleteCourseAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearError, clearCurrentCourse } = coursesSlice.actions;
export default coursesSlice.reducer;
